// ImportReport — execution report display for completed/failed imports.
// Shows summary bar, section-by-section breakdown, per-item status.

import { useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { useTenant } from '@/components/shared/TenantShell'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  CheckCircle2,
  XCircle,
  AlertTriangle,
  ChevronRight,
  ChevronDown,
  Upload,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import type { PendingImport } from '@/lib/api/imports'

// --- Types from backend Report struct ---

interface SectionResult {
  section: string
  total: number
  succeeded: number
  failed: number
  skipped: number
  errors?: ItemError[]
}

interface ItemError {
  section: string
  token: string
  operation?: string
  status?: number
  message?: string
  reason?: string
}

interface ReportData {
  start_time: string
  end_time: string
  input_file?: string
  tenant_id?: string
  dry_run?: boolean
  sections: SectionResult[]
  total_items: number
  succeeded: number
  failed: number
  skipped: number
  error?: string
}

// --- Props ---

interface ImportReportProps {
  importData: Pick<PendingImport, 'id' | 'status' | 'execution_report'>
}

// --- Component ---

export function ImportReport({ importData }: ImportReportProps) {
  const { slug } = useTenant()
  const navigate = useNavigate()
  const isSuccess = importData.status === 'completed'

  const report = importData.execution_report as ReportData | null | undefined

  return (
    <div className="mx-auto max-w-3xl space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        {isSuccess ? (
          <CheckCircle2 className="h-8 w-8 text-green-600" />
        ) : (
          <XCircle className="h-8 w-8 text-destructive" />
        )}
        <div>
          <h1 className="text-2xl font-semibold">
            {isSuccess ? 'Import Complete' : 'Import Failed'}
          </h1>
          <p className="text-sm text-muted-foreground">
            Import ID: {importData.id.substring(0, 8)}...
            {report?.start_time && (
              <span className="ml-2">
                · {new Date(report.start_time).toLocaleString()}
              </span>
            )}
          </p>
        </div>
      </div>

      {/* Summary bar */}
      {report && (
        <div className="grid grid-cols-4 gap-3">
          <SummaryCard label="Total" count={report.total_items} variant="neutral" />
          <SummaryCard label="Created" count={report.succeeded} variant="success" />
          <SummaryCard label="Skipped" count={report.skipped} variant="warning" />
          <SummaryCard label="Failed" count={report.failed} variant="error" />
        </div>
      )}

      {/* Top-level error */}
      {report?.error && (
        <div className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/10 p-3">
          <XCircle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
          <div>
            <p className="text-sm font-medium text-destructive">Execution Error</p>
            <p className="mt-1 text-sm text-destructive/90">{report.error}</p>
          </div>
        </div>
      )}

      {/* Duration */}
      {report?.start_time && report?.end_time && (
        <p className="text-xs text-muted-foreground">
          Duration:{' '}
          {formatDuration(
            new Date(report.end_time).getTime() - new Date(report.start_time).getTime(),
          )}
        </p>
      )}

      {/* Section breakdown */}
      {report?.sections && report.sections.length > 0 && (
        <div className="space-y-2">
          <h2 className="text-sm font-semibold">Section Breakdown</h2>
          {report.sections.map((section, i) => (
            <SectionRow key={i} section={section} />
          ))}
        </div>
      )}

      {/* No report data fallback */}
      {!report && (
        <div className="rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground">
          <p>No execution report data available.</p>
        </div>
      )}

      {/* Actions */}
      <div className="flex gap-3 pt-2">
        <Button onClick={() => navigate({ to: `/t/${slug}/import` })}>
          <Upload className="mr-1.5 h-4 w-4" />
          Start New Import
        </Button>
      </div>
    </div>
  )
}

// --- Summary card ---

function SummaryCard({
  label,
  count,
  variant,
}: {
  label: string
  count: number
  variant: 'neutral' | 'success' | 'warning' | 'error'
}) {
  const styles = {
    neutral: 'border-border',
    success: 'border-green-200 bg-green-50',
    warning: 'border-amber-200 bg-amber-50',
    error: 'border-destructive/30 bg-destructive/10',
  }

  const textStyles = {
    neutral: 'text-foreground',
    success: 'text-green-700',
    warning: 'text-amber-700',
    error: 'text-destructive',
  }

  return (
    <div className={cn('rounded-lg border p-3 text-center', styles[variant])}>
      <p className={cn('text-2xl font-bold', textStyles[variant])}>{count}</p>
      <p className="text-xs text-muted-foreground">{label}</p>
    </div>
  )
}

// --- Section row (collapsible) ---

function SectionRow({ section }: { section: SectionResult }) {
  const [expanded, setExpanded] = useState(
    // Auto-expand sections with errors
    (section.failed ?? 0) > 0 || (section.skipped ?? 0) > 0,
  )

  const hasErrors = (section.errors?.length ?? 0) > 0
  const allSucceeded = section.succeeded === section.total && section.total > 0
  const allFailed = section.succeeded === 0 && section.total > 0

  return (
    <div className="rounded-md border">
      <button
        type="button"
        className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-accent/50"
        onClick={() => setExpanded(!expanded)}
      >
        {hasErrors ? (
          expanded ? (
            <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
          ) : (
            <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
          )
        ) : (
          <span className="h-4 w-4 shrink-0" />
        )}

        {/* Status icon */}
        {allSucceeded && <CheckCircle2 className="h-4 w-4 shrink-0 text-green-600" />}
        {allFailed && <XCircle className="h-4 w-4 shrink-0 text-destructive" />}
        {!allSucceeded && !allFailed && section.total > 0 && (
          <AlertTriangle className="h-4 w-4 shrink-0 text-amber-600" />
        )}

        <span className="flex-1 font-medium">{section.section}</span>

        <span className="shrink-0 text-xs text-muted-foreground">
          {section.succeeded}/{section.total}
        </span>

        {section.failed > 0 && (
          <Badge variant="destructive" className="ml-1 text-[10px]">
            {section.failed} failed
          </Badge>
        )}
        {section.skipped > 0 && (
          <Badge variant="outline" className="ml-1 text-[10px]">
            {section.skipped} skipped
          </Badge>
        )}
      </button>

      {expanded && hasErrors && (
        <div className="border-t px-3 py-2 space-y-1">
          {section.errors!.map((err, i) => (
            <ErrorRow key={i} error={err} />
          ))}
        </div>
      )}
    </div>
  )
}

// --- Error row ---

function ErrorRow({ error }: { error: ItemError }) {
  const displayMessage = error.reason || error.message || 'Unknown error'

  return (
    <div className="flex items-start gap-2 text-xs">
      <XCircle className="mt-0.5 h-3 w-3 shrink-0 text-destructive" />
      <div>
        <span className="font-medium">{error.token}</span>
        {error.operation && (
          <span className="ml-1 text-muted-foreground">{error.operation}</span>
        )}
        {error.status ? (
          <span className="ml-1 text-muted-foreground">(HTTP {error.status})</span>
        ) : null}
        <p className="text-muted-foreground">{displayMessage}</p>
      </div>
    </div>
  )
}

// --- Helpers ---

function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`
  const seconds = Math.round(ms / 100) / 10
  return `${seconds}s`
}
